import { HomeDao, atomWithLoadMore } from '@rock/db-admin';
import type { Home, HomeId } from '@rock/types';
import { shallowEqual } from '@rock/util';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

const homeDao = new HomeDao();

type HomesFilter = { active: boolean; namePrefix: string; includeTest: boolean };

/**
 * Paginated list of homes. `includeTest` is applied in the query rather than to the loaded page, so a
 * page of N is N real homes — and so this list agrees with the Dashboard's Homes stat, which counts
 * real homes only.
 */
export const homesState = atomWithLoadMore<Home, HomesFilter>(
    ({ limitAtom, filterAtom }) => {
        const activeAtom = atom((get) => get(filterAtom).active);
        const namePrefixAtom = atom((get) => get(filterAtom).namePrefix);
        const includeTestAtom = atom((get) => get(filterAtom).includeTest);
        return homeDao.homesAtom(activeAtom, namePrefixAtom, limitAtom, includeTestAtom);
    },
    { initialFilter: { active: true, namePrefix: '', includeTest: false } },
);

export const activeHomesState = homeDao.homesAtom(true);

/**
 * The most recently created real homes — the Dashboard's Recent Homes card. Test homes are excluded
 * by the query itself, so the 8 fetched are the 8 shown.
 */
export const recentHomesState = homeDao.recentHomesAtom(8);

/** Total active real homes (server aggregation, one-shot) — the Dashboard's Homes stat. */
export const activeHomesCountState = atom(async () => homeDao.countActiveHomes());

export const homeState = atomFamily((homeId: HomeId) => homeDao.homeAtom(homeId), shallowEqual);

export const currentHomeIdState = atom<HomeId | undefined>(undefined);

export const currentHomeState = homeDao.homeAtom(currentHomeIdState);
